import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

import ArrowIcon from "@/design-system/icons/arrow-icon";
import { useViewportInfoContext } from "@/src/contexts";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { Device } from "@Components/Modules/ViewportResize";
import { searchNavigationId } from "@Constants/PortalConstants";
import * as stylex from "@stylexjs/stylex";

import { getUrlParameterByName } from "../common/Helpers";
import { TransitionLink } from "../common/Transition";
import { ArrowBackLink } from "../common/links/ArrowBackLink";
import { colors } from "./../../styles/tokens.stylex";
import { add } from "./DiscountNavigationLib";

import * as cssPortal from "../shared/Portal.module.css";
import * as css from "./DiscountNavigation.module.css";

export interface IDiscountNavigation extends IComponentAjax<IDiscountNavigationResultExtension> {
    storageKeyParameterName: string;
    groupKeyParameterName: string;
    returnParameterName: string;
    description: string;
    id: string;
    searchLabel: string;
    pageTransition: (url: string) => void;
    device: Device;
}

interface IDiscountNavigationState extends IComponentAjaxState {
    title: string;
    validityDate: string;
    description: string;
    adjacentItems: { previousItem: IDiscountNavigationItem; nextItem: IDiscountNavigationItem };
    totalItems: number;
    itemPosition: number;
    initialized: boolean;
}

interface IDiscountNavigationItemPosition {
    storedItem: IDiscountNavigationStorageItem;
    itemPosition: number;
    previousItem: IDiscountNavigationItem;
    nextItem: IDiscountNavigationItem;
}

interface IDiscountNavigationItem {
    id: string;
    title: string;
    validityDate: string;
    groupKey: string;
    targetUrl: string;
    previousItem?: IDiscountNavigationItem;
    nextItem?: IDiscountNavigationItem;
}

interface IDiscountNavigationStorageItem {
    title: string;
    totalItems: number;
    items: IDiscountNavigationItem[];
    ajaxAction: string;
}

export interface IDiscountNavigationResult {
    storageKey: string;
    append: boolean;
    storageItem: IDiscountNavigationStorageItem;
}

export interface IDiscountNavigationResultExtension {
    navigation: IDiscountNavigationResult;
}

class DiscountNavigation extends ComponentAjax<
    IDiscountNavigationResultExtension,
    IDiscountNavigation,
    IDiscountNavigationState
> {
    public static defaultProps: IDiscountNavigation = {
        ...ComponentAjax.defaultProps,
        ajaxAction: "{ajaxAction}",
        storageKeyParameterName: null,
        groupKeyParameterName: null,
        returnParameterName: null,
        description: null,
        searchLabel: "",
        id: null,
        device: Device.Desktop,
        pageTransition: null,
    };

    constructor(props: IDiscountNavigation) {
        super(props);

        this.state = {
            isLoading: false,
            title: null,
            validityDate: "",
            description: "",
            adjacentItems: null,
            totalItems: 0,
            itemPosition: 0,
            initialized: false,
        };
    }

    public componentDidMount(): void {
        this.initialize();
    }

    public render(): React.ReactNode {
        if (!this.props.storageKeyParameterName || !this.props.id || this.state.initialized === false) {
            return null;
        }

        const adjs = this.state.adjacentItems;

        if (!adjs) {
            return null;
        }

        let prevLink: string = null;
        let nextLink: string = null;
        let backLink: string = null;

        if (adjs.previousItem) {
            prevLink = adjs.previousItem.targetUrl;
        }

        if (adjs.nextItem) {
            nextLink = adjs.nextItem.targetUrl;
        }

        const returnKey = this.getReturnUrl();
        if (returnKey) {
            backLink = returnKey;
        }

        const getLeftNavigation = () => {
            if (prevLink === null) {
                return null;
            }

            return (
                <TransitionLink navigateUrl={prevLink}>
                    <ArrowIcon direction="left" styleX={styles.arrow} />
                </TransitionLink>
            );
        };
        const getRightNavigation = () => {
            if (nextLink === null) {
                return null;
            }

            return (
                <TransitionLink navigateUrl={nextLink}>
                    <ArrowIcon direction="right" styleX={styles.arrow} />
                </TransitionLink>
            );
        };

        const searchNavigationElement = document.getElementById(searchNavigationId)!;
        return createPortal(
            <div className={css.discountNavigationComponent}>
                <div className={cssPortal.standardContainer}>
                    <div className={css.navigationInfo}>
                        {backLink && (
                            <TransitionLink className={css.navigationBack} navigateUrl={backLink}>
                                <ArrowBackLink className={css.searchBack} />
                                <div className={css.textContainer}>
                                    <span className={css.description}>{this.state.description}</span>
                                    {this.state.validityDate && (
                                        <span
                                            className={css.validityDate}
                                            dangerouslySetInnerHTML={{
                                                __html: this.state.validityDate,
                                            }}
                                        />
                                    )}
                                </div>
                            </TransitionLink>
                        )}
                        <div className={css.navigationActions}>
                            {this.state.title ? (
                                <span
                                    className={css.title}
                                    dangerouslySetInnerHTML={{
                                        __html: `${this.props.searchLabel} <b>''${this.state.title}''</b>`,
                                    }}
                                />
                            ) : null}
                            {getLeftNavigation()}
                            {this.getItemCounter()}
                            {getRightNavigation()}
                        </div>
                    </div>
                </div>
            </div>,
            searchNavigationElement,
        );
    }

    protected ajaxSuccess(response: IDiscountNavigationResultExtension): void {
        add(response.navigation);

        const item = this.getItem();
        this.updateState(item);
    }

    protected ajaxFail(): boolean {
        return true;
    }

    private initialize(): void {
        const result = this.getItem();

        if (!result) {
            return;
        }

        this.updateState(result);

        const maxItemsStored = result.storedItem.items.length;
        if (
            result.storedItem.ajaxAction &&
            (result.itemPosition === maxItemsStored || result.itemPosition === maxItemsStored - 1)
        ) {
            this.ajaxRequest(result.storedItem);
        } else {
            this.setState({ initialized: true });
        }
    }

    private updateState(navigationItem: IDiscountNavigationItemPosition): void {
        const description = navigationItem.storedItem.items[navigationItem.itemPosition - 1].title;
        const { validityDate } = navigationItem.storedItem.items[navigationItem.itemPosition - 1];

        this.setState({
            adjacentItems: {
                previousItem: navigationItem.previousItem,
                nextItem: navigationItem.nextItem,
            },
            totalItems: navigationItem.storedItem.totalItems,
            itemPosition: navigationItem.itemPosition,
            title: navigationItem.storedItem.title,
            validityDate,
            description: description || this.props.description,
            initialized: true,
        });
    }

    private getReturnUrl(): string {
        const regex = new RegExp(`[?&]${this.props.returnParameterName}=(.*?)$`);
        const results = regex.exec(window.location.href);

        if (!results) {
            return null;
        }

        if (!results[1]) {
            return "";
        }

        return decodeURI(decodeURIComponent(results[1].replace(/\+/g, " ")));
    }

    private getItem(): IDiscountNavigationItemPosition {
        if (!window.localStorage) {
            return null;
        }

        const storageKey = getUrlParameterByName(this.props.storageKeyParameterName);

        if (!storageKey) {
            return null;
        }

        const storedItemValue = localStorage.getItem(storageKey);

        if (!storedItemValue) {
            return null;
        }

        let previousItem: IDiscountNavigationItem = null;
        let nextItem: IDiscountNavigationItem = null;

        const storedItem: IDiscountNavigationStorageItem = JSON.parse(storedItemValue);

        if (!storedItem.items) {
            return null;
        }

        let itemIndex = -1;

        const groupKey = getUrlParameterByName(this.props.groupKeyParameterName);

        storedItem.items.some((item: IDiscountNavigationItem, index: number) => {
            if (!item) {
                return false;
            }

            const isInGroup = groupKey ? groupKey === item.groupKey : true;
            if (isInGroup && item.id.toUpperCase() === this.props.id.toUpperCase()) {
                if (index > 0) {
                    previousItem = this.getNavigationItem(storedItem.items, index, false);
                }

                if (index + 1 < storedItem.items.length) {
                    nextItem = this.getNavigationItem(storedItem.items, index, true);
                }

                itemIndex = index;

                return true;
            }
            return false;
        });

        if (itemIndex >= 0) {
            return {
                storedItem,
                itemPosition: itemIndex + 1,
                previousItem,
                nextItem,
            };
        }

        return null;
    }

    private getNavigationItem(
        items: IDiscountNavigationItem[],
        index: number,
        nextItem: boolean,
    ): IDiscountNavigationItem {
        const inc = nextItem === true ? 1 : -1;

        if (this.props.device === Device.Mobile || this.props.device === Device.Tablet) {
            return items[index + inc];
        }

        for (let i = index + inc; i >= 0 && i < items.length; i += inc) {
            const regex = /[?&]flyerPage(=([^&#]*)|&|#|$)/;
            let result = regex.exec(items[index].targetUrl);

            if (!result || !result[2]) {
                return items[i];
            }

            result = regex.exec(items[i].targetUrl);

            if (!result || !result[2]) {
                return items[i];
            }

            const itemPage = parseInt(result[2], 10);

            let valuePage = parseInt(result[2], 10);
            valuePage = valuePage % 2 === 0 ? valuePage - 1 : valuePage + 1;

            if (valuePage !== itemPage) {
                return items[i];
            }
        }

        return null;
    }

    private getItemCounter(): React.ReactNode {
        if (this.state.totalItems <= 0 || this.state.itemPosition <= 0) {
            return null;
        }

        return `${this.state.itemPosition}/${this.state.totalItems}`;
    }
}

const DiscountNavigationConnect = (props: IDiscountNavigation) => {
    const { device } = useViewportInfoContext();
    const { pathname, search } = useLocation();

    return <DiscountNavigation key={pathname + search} {...props} device={device} />;
};

export { DiscountNavigationConnect as DiscountNavigation };

const styles = stylex.create({
    arrow: {
        strokeWidth: 1,
        color: colors.fg_Primary,
    },
});
